import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { getApiBaseUrl } from '../../services/api';

const API_BASE_URL = getApiBaseUrl();

interface Admin {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  is_super_admin: boolean;
  created_at: string;
}

const AdminManagement: React.FC = () => {
  const { user } = useAuth();
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    first_name: '',
    last_name: '',
    password: ''
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const adminUsers = response.data.users.filter((u: any) => u.role_name === 'admin');
      setAdmins(adminUsers);
    } catch (err) {
      console.error('Error fetching admins:', err);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${API_BASE_URL}/admin/create-admin`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setSuccess('Admin created successfully!');
      setFormData({ email: '', phone: '', first_name: '', last_name: '', password: '' });
      setShowCreateForm(false);
      fetchAdmins();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  if (!user?.is_super_admin) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-yellow-50 border border-yellow-200 rounded-volexx p-6">
          <h2 className="text-xl font-bold text-yellow-800 mb-2 font-poppins">Access Restricted</h2>
          <p className="text-yellow-700 font-poppins">
            Only super admins can access admin management. Please contact your super administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 font-poppins">Admin Management</h1>
        <p className="text-gray-600 mt-2 font-poppins">
          Create and manage admin users (Super Admin Only)
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-volexx font-poppins">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-volexx font-poppins">
          {success}
        </div>
      )}

      <div className="mb-6">
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="bg-volexx-blue-500 hover:bg-volexx-blue-600 text-white font-semibold py-2 px-6 rounded-volexx transition-colors font-poppins"
        >
          {showCreateForm ? 'Cancel' : '+ Create New Admin'}
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-volexx shadow-sm p-6 mb-8 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4 font-poppins">Create New Admin</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="first_name"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                  value={formData.first_name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="last_name"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                  value={formData.last_name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="password"
                  required
                  minLength={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-volexx-blue-500 font-poppins"
                  value={formData.password}
                  onChange={handleChange}
                />
                <p className="text-sm text-gray-500 mt-1 font-poppins">Minimum 6 characters</p>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="bg-volexx-orange-500 hover:bg-volexx-orange-600 text-white font-bold py-2 px-6 rounded-volexx transition-colors disabled:opacity-50 font-poppins"
            >
              {loading ? 'Creating...' : 'Create Admin'}
            </button>
          </form>
        </div>
      )}

      <div className="bg-white rounded-volexx shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 font-poppins">Existing Admins</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider font-poppins">
                  Created
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {admins.map((admin) => (
                <tr key={admin.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-poppins">
                    <div className="font-medium text-gray-900">
                      {admin.first_name} {admin.last_name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 font-poppins">
                    {admin.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 font-poppins">
                    {admin.phone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap font-poppins">
                    {admin.is_super_admin ? (
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                        Super Admin
                      </span>
                    ) : (
                      <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        Admin
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600 font-poppins">
                    {new Date(admin.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;
